import { useRef } from 'react';
import { useParams, useSearch, useNavigate } from '@tanstack/react-router';
import { FolderPlus, Upload } from 'lucide-react';
import type { AssetKind } from '@shared/types';
import { AssetGrid } from '@/components/AssetGrid';
import { Button, CenteredSpinner, EmptyState, ErrorState } from '@/components/ui';
import { cn } from '@/lib/format';
import { useAssets, useCreateFolder, useFolders, useUpload } from '@/lib/queries';

const KINDS: (AssetKind | 'all')[] = ['all', 'image', 'document', 'video', 'audio', 'archive'];

export interface BrowseSearch {
  kind?: AssetKind;
  sort?: string;
  order?: 'asc' | 'desc';
  untagged?: boolean;
  favourites?: boolean;
}

/** The root "All Assets" view, the smart views, and the per-folder view share this. */
export function BrowsePage() {
  // folderId comes from the route param on /folders/$folderId; root has none.
  const params = useParams({ strict: false }) as { folderId?: number };
  const folderId = params.folderId;
  const search = useSearch({ strict: false }) as BrowseSearch;
  const navigate = useNavigate();

  const kind = search.kind ?? 'all';
  const sort = search.sort ?? 'name';

  const { data: folders } = useFolders();
  const folder = folderId ? folders?.items.find((f) => f.id === folderId) : undefined;

  const query = useAssets({
    folderId,
    recursive: folderId === undefined ? true : true,
    kind: search.kind,
    untagged: search.untagged,
    favourites: search.favourites,
    sort,
    order: search.order,
    perPage: 200,
  });

  const upload = useUpload(folderId ?? null);
  const createFolder = useCreateFolder();
  const fileInput = useRef<HTMLInputElement>(null);

  // Update a single search param, preserving the folder route if any.
  function setParam(patch: Partial<BrowseSearch>) {
    void navigate({ to: '.', search: (prev: BrowseSearch) => ({ ...prev, ...patch }) as never });
  }

  const title = folder
    ? folder.name
    : search.favourites ? 'Favourites'
    : search.untagged ? 'Untagged'
    : search.kind ? `${search.kind[0]!.toUpperCase()}${search.kind.slice(1)}s`
    : search.sort === 'created' ? 'Recent'
    : 'All Assets';

  const subtitle = folder?.path ?? 'Entire library';

  async function handleFiles(files: FileList | null) {
    if (!files) return;
    for (const f of Array.from(files)) await upload.mutateAsync(f).catch(() => {});
    if (fileInput.current) fileInput.current.value = '';
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b line surface-card px-5 py-3">
        <div className="mr-auto min-w-0">
          <h1 className="truncate text-base font-semibold text-strong">{title}</h1>
          <p className="text-xs text-muted">
            {subtitle}
            {query.data ? ` · ${query.data.total} assets` : ''}
          </p>
        </div>

        <div className="flex rounded-lg surface-sunken p-0.5">
          {KINDS.map((k) => (
            <button
              key={k}
              onClick={() => setParam({ kind: k === 'all' ? undefined : (k as AssetKind) })}
              className={cn('rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors', kind === k ? 'surface-card text-strong shadow-sm' : 'text-muted hover:text-strong')}
            >
              {k === 'all' ? 'All' : k}
            </button>
          ))}
        </div>

        <select value={sort} onChange={(e) => setParam({ sort: e.target.value })} className="rounded border line surface-card px-1.5 py-1 text-xs text-strong">
          <option value="name">Name</option>
          <option value="created">Newest</option>
          <option value="size">Size</option>
          <option value="kind">Type</option>
        </select>

        <Button variant="secondary" className="h-8" onClick={() => createFolder.mutate({ name: 'New folder', parentId: folderId ?? null })}>
          <FolderPlus className="size-3.5" /> Folder
        </Button>
        <Button variant="primary" className="h-8" loading={upload.isPending} onClick={() => fileInput.current?.click()}>
          <Upload className="size-3.5" /> Upload
        </Button>
        <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => void handleFiles(e.target.files)} />
      </header>

      <div
        className="scroll-thin min-h-0 flex-1 overflow-y-auto"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void handleFiles(e.dataTransfer.files);
        }}
      >
        {query.isPending ? (
          <CenteredSpinner label="Loading assets…" />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (query.data?.items.length ?? 0) === 0 ? (
          <EmptyState icon={<Upload />} title="No assets" description={'Drag files here or click “Upload”.'} />
        ) : (
          <AssetGrid assets={query.data!.items} />
        )}
      </div>
    </div>
  );
}
